import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Callout from 'tio-ui/components/callout';
import { PhWarningCircle } from 'ember-phosphor-icons';
import type { CardDetailsFailure } from 'tio-employee/utils/tas/course-cash/stripe-issuing-session';

interface CardDetailsUnavailableSignature {
  Args: {
    /** Why the card's details can't be shown; renders nothing while null. */
    failure: CardDetailsFailure | null;
    /** Runs the credential exchange again. Offered only for a `retryable` failure. */
    onRetry: () => void;
    /** Shows Retry as busy while that exchange runs. */
    isRetrying: boolean;
  };
  Element: HTMLDivElement;
}

/** Stands in for a card view's Stripe details while they're unavailable, saying why. */
const CardDetailsUnavailable: TOC<CardDetailsUnavailableSignature> = <template>
  {{#if @failure}}
    <Callout @intent="warning" @align="center" role="status" ...attributes>
      <:icon><PhWarningCircle @size={{24}} @weight="fill" /></:icon>
      <:default>
        <p>
          {{#if (eq @failure "denied")}}
            {{t "tas.stripe_details.card_details_denied"}}
          {{else if (eq @failure "expired")}}
            {{t "tas.stripe_details.card_details_expired"}}
          {{else}}
            {{t "tas.stripe_details.card_details_unavailable"}}
          {{/if}}
        </p>
      </:default>
      <:action>
        {{#if (eq @failure "retryable")}}
          <Button
            @appearance="outlined"
            @size="sm"
            @isRunning={{@isRetrying}}
            @testHook="card-details__retry"
            {{on "click" @onRetry}}
            data-test-card-details-retry
          >
            {{t "common.records.retry"}}
          </Button>
        {{/if}}
      </:action>
    </Callout>
  {{/if}}
</template>;

export default CardDetailsUnavailable;
